// Reminders (route /more/reminders) — every kind, its time, and the two
// global controls above them.
//
// Its own screen rather than a row in Preferences, for the reason the settings
// hub exists at all: this is six toggles, two times and a promise, and folding
// that into the general page would bury it.
//
// The line at the bottom is a feature, not a disclaimer. Every competitor sends
// more and says nothing about it.
import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { useT, useLocale, apiLang } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { SettingsGroup, IconChip, Toggle } from "../components/ui/SettingsList";
import { PageHeader } from "../components/ui/PageHeader";
import { pushSupported, isIos, isStandalone, enablePush, disablePush, browserTz } from "../lib/push";
import { resyncReminders } from "../lib/reminderPrefs";
import { cancelAllReminders } from "../lib/reminderScheduler";
import { isNativeApp } from "../lib/native";
import { REMINDER_KINDS, MAX_PER_DAY, type ReminderKind } from "@shared/notifications";
import type { ReminderPrefsResponse, ReminderSetting } from "@shared/types";

const TINT = "#f59e0b";

/** The check-in arrives when the week's data is ready, so it has no clock. */
const TIMELESS: ReminderKind[] = ["check_in_ready"];

export function MoreReminders() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();

  const [prefs, setPrefs] = useState<ReminderPrefsResponse | null>(null);
  const [busy, setBusy] = useState(false);

  const available = isNativeApp() || (pushSupported() && !(isIos() && !isStandalone()));
  const texts = {
    title: (kind: string) => t(`reminders.${kind}.title`),
    body: (kind: string) => t(`reminders.${kind}.body`),
  };

  useEffect(() => { api.getReminders().then(setPrefs).catch(() => setPrefs(null)); }, []);

  const setting = (kind: ReminderKind): ReminderSetting =>
    prefs?.settings.find((s) => s.kind === kind)
    ?? { kind, enabled: false, local_time: "08:00", tz: browserTz() };

  const anyEnabled = (p: ReminderPrefsResponse) => p.settings.some((s) => s.enabled);

  async function save(update: () => Promise<void>, next: ReminderPrefsResponse) {
    setBusy(true);
    const previous = prefs;
    setPrefs(next); // the toggle moves under the thumb, not after a round trip
    try {
      await update();
      await resyncReminders(texts, apiLang(locale));
    } catch (e) {
      setPrefs(previous);
      const msg = (e as Error)?.message ?? "";
      toast.error(
        msg === "permission-denied" ? t("reminders.denied")
        : msg === "no-vapid-key" ? t("reminders.not_configured")
        : t("toast.failed"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function toggleMaster(on: boolean) {
    if (!prefs) return;
    await save(async () => {
      if (on && anyEnabled(prefs)) await enablePush();
      await api.putReminderPrefs({ master: on });
      // Turning everything off has to clear what is already scheduled — a
      // pending local notification does not care that a switch moved.
      if (!on) await cancelAllReminders();
    }, { ...prefs, master: on });
  }

  async function toggleKind(kind: ReminderKind, on: boolean) {
    if (!prefs) return;
    const updated: ReminderSetting = { ...setting(kind), enabled: on, tz: browserTz() };
    const next = {
      ...prefs,
      settings: prefs.settings.map((s) => (s.kind === kind ? updated : s)),
    };
    await save(async () => {
      // Permission is asked for here, at the moment someone asks for a
      // reminder — never on first launch, and never before there is anything
      // to grant it for.
      if (on) await enablePush();
      await api.putReminder(updated);
      if (!on && !anyEnabled(next)) await disablePush();
    }, next);
  }

  async function changeTime(kind: ReminderKind, local_time: string) {
    if (!prefs || !local_time) return;
    const updated: ReminderSetting = { ...setting(kind), local_time, tz: browserTz() };
    await save(
      () => api.putReminder(updated).then(() => {}),
      { ...prefs, settings: prefs.settings.map((s) => (s.kind === kind ? updated : s)) },
    );
  }

  async function changeQuiet(field: "quiet_from" | "quiet_to", value: string) {
    if (!prefs || !value) return;
    await save(
      () => api.putReminderPrefs({ [field]: value }).then(() => {}),
      { ...prefs, [field]: value },
    );
  }

  if (!available) {
    return (
      <div className="space-y-6 pt-4 animate-rise">
        <PageHeader back="history" title={t("reminders.title")} />
        <SettingsGroup>
          <div className="flex items-center gap-3 px-4 py-3.5">
            <IconChip icon={Bell} tint={TINT} />
            <p className="text-base text-fg-muted flex-1">
              {!pushSupported() ? t("reminders.unsupported") : t("reminders.ios_install")}
            </p>
          </div>
        </SettingsGroup>
      </div>
    );
  }

  const on = prefs?.master ?? true;

  return (
    <div className="space-y-6 pt-4 animate-rise">
      <PageHeader back="history" title={t("reminders.title")} />

      <SettingsGroup>
        <div className="flex items-center gap-3 px-4 py-3.5">
          <IconChip icon={Bell} tint={TINT} />
          <div className="flex-1 min-w-0">
            <div className="text-base">{t("reminders.master")}</div>
            <div className="text-sm text-fg-muted">{t("reminders.master_hint")}</div>
          </div>
          <Toggle
            checked={on}
            onChange={toggleMaster}
            disabled={busy || !prefs}
            label={t("reminders.master")}
          />
        </div>
      </SettingsGroup>

      {on && (
        <SettingsGroup>
          {REMINDER_KINDS.map((kind) => {
            const s = setting(kind);
            return (
              <div key={kind} className="flex items-center gap-3 px-4 py-3.5">
                <div className="flex-1 min-w-0">
                  <div className="text-base">{t(`reminders.kind.${kind}`)}</div>
                  <div className="text-sm text-fg-muted">
                    {TIMELESS.includes(kind) ? t("reminders.when_ready") : s.local_time}
                  </div>
                </div>
                {s.enabled && !TIMELESS.includes(kind) && (
                  <input
                    type="time"
                    value={s.local_time}
                    onChange={(e) => changeTime(kind, e.target.value)}
                    disabled={busy}
                    aria-label={t("reminders.time")}
                    className="input w-28 tabular-nums"
                  />
                )}
                <Toggle
                  checked={s.enabled}
                  onChange={(next) => toggleKind(kind, next)}
                  disabled={busy || !prefs}
                  label={t(`reminders.kind.${kind}`)}
                />
              </div>
            );
          })}
        </SettingsGroup>
      )}

      {on && (
        <SettingsGroup>
          <div className="px-4 py-3.5 space-y-2">
            <div className="text-base">{t("reminders.quiet_hours")}</div>
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={prefs?.quiet_from ?? "22:00"}
                onChange={(e) => changeQuiet("quiet_from", e.target.value)}
                disabled={busy}
                aria-label={t("reminders.quiet_from")}
                className="input flex-1 tabular-nums"
              />
              <span className="text-fg-muted">–</span>
              <input
                type="time"
                value={prefs?.quiet_to ?? "07:00"}
                onChange={(e) => changeQuiet("quiet_to", e.target.value)}
                disabled={busy}
                aria-label={t("reminders.quiet_to")}
                className="input flex-1 tabular-nums"
              />
            </div>
          </div>
        </SettingsGroup>
      )}

      <p className="px-4 text-sm text-fg-muted leading-snug">
        {t("reminders.promise", { n: String(MAX_PER_DAY) })}
      </p>
    </div>
  );
}
